/**
 * Marp endpoint wrappers (Wave 2D, May 2026 — Marp migration; extended
 * Wave 4 with template-management + dual-download helpers).
 *
 * Backs Agent B's `/api/v1/marp/{content_id}/...` surface:
 *
 *   GET  /api/v1/marp/{id}/markdown                       -> text/plain (Marp source)
 *   PUT  /api/v1/marp/{id}/markdown                       -> 204 (update source)
 *   GET  /api/v1/marp/{id}/preview.html                   -> text/html (iframe-only)
 *   POST /api/v1/marp/{id}/ai-rewrite                     -> JSON rewrite payload
 *   GET  /api/v1/marp/{id}/download/frozen.pptx           -> bytes (Wave 4 — Marp-rendered)
 *   GET  /api/v1/marp/{id}/download/editable.pptx         -> bytes (Wave 4 — Pandoc-rendered)
 *
 * Wave 4 also adds the template-management surface (BE-Templates agent):
 *
 *   GET  /api/v1/templates/marp                           -> { builtins, customs }
 *   POST /api/v1/templates/marp/upload-css                -> { slug }
 *   POST /api/v1/templates/marp/extract-from-pptx         -> { css, suggested_slug, tokens }
 *
 * The iframe-only routes (`preview.html`, `thumbs/*.png`, the two
 * `download/*.pptx` URLs) are consumed via raw `<iframe src>` / `<a href>`
 * and do NOT go through this module — cookie auth carries them, and
 * bypassing `apiClient` keeps the binary fetch off the JSON parser. The
 * `getDownloadUrl` helper below builds the absolute URL so anchors can
 * fire off a real cross-origin download in the Tauri shell where
 * `window.location.origin === "tauri://localhost"`.
 *
 * `apiClient` already prepends the `/api/v1` base (`getEnv().apiUrl`),
 * so the paths here are written WITHOUT the `/api/v1` prefix to match the
 * convention used by `core/api/endpoints/workspace.ts` et al.
 */

import { apiClient } from "@ando/core/api";

/**
 * Marp source markdown — fetched lazily by `useMarpMarkdown` when the
 * artifact pane decides a doc might be marp-generated.
 *
 * The BE returns `text/plain`; `ApiClient.parseSuccessBody` falls back to
 * returning the raw text body when JSON.parse fails, so a plain
 * `apiClient.get<string>` is the SOTA way to consume it without inventing
 * a parallel text-fetch helper.
 */
export async function getMarpMarkdown(contentId: string): Promise<string> {
  return await apiClient.get<string>(
    `/marp/${encodeURIComponent(contentId)}/markdown`,
  );
}

/**
 * Push the current draft markdown back to the BE so the iframe preview
 * tab (`/api/v1/marp/{id}/preview.html`) renders the fresh source on the
 * next reload, and the Confirm flow (BE `_execute_marp_deck`) sees the
 * user's last edits — not the original LLM-emitted markdown.
 *
 * Called debounced (~1500ms) from `MarpDeckEditorMount`. The endpoint is
 * idempotent: the BE replaces the `<key>.marp.md` MinIO object in place.
 */
export async function putMarpMarkdown(
  contentId: string,
  markdown: string,
): Promise<void> {
  await apiClient.request<void>(
    `/marp/${encodeURIComponent(contentId)}/markdown`,
    {
      method: "PUT",
      headers: { "Content-Type": "text/plain" },
      body: markdown,
    },
  );
}

// ─── Wave 9 — outline graphical preview ─────────────────────────────────

/**
 * A single slide of the per-slide outline (mirror of the ``@ando/ui``
 * ``SlideObj`` + the BE ``slide_list`` field value shape). Kept local so
 * this module stays free of a ``@ando/ui`` import (it lives in the apps
 * layer alongside the other marp wrappers).
 */
export interface MarpPreviewSlide {
  readonly title: string;
  readonly body: string;
  readonly layout: string;
  readonly image_prompt: string | null;
}

/**
 * Body for ``POST /api/v1/marp/preview-thumbnails``.
 *
 * Sources are mutually-complementary; the BE applies a precedence chain
 * ``markdown`` > ``slides`` > ``content_id`` > ``proposal_id``:
 *   - ``markdown``: raw Marp source — reflects the editor's live, unsaved
 *     buffer (used by ``MarpDeckEditor``'s Anteprima tab).
 *   - ``slides``: the per-slide outline (used by the proposal surface).
 *   - ``content_id``: a persisted deck — BE loads the sidecar markdown.
 *   - ``proposal_id``: BE loads the outline server-side.
 */
export interface MarpPreviewRequest {
  /** Per-slide outline (proposal surface). */
  readonly slides?: ReadonlyArray<MarpPreviewSlide>;
  /** Raw Marp markdown — reflects live, unsaved editor edits. */
  readonly markdown?: string;
  /** Persisted deck id — BE loads the sidecar markdown when no buffer. */
  readonly content_id?: string;
  /** Optional template slug to render the outline against. */
  readonly template_name?: string;
  /** Optional proposal id — BE loads the outline server-side when ``slides`` is empty. */
  readonly proposal_id?: string;
  /** Optional deck title (filename) forwarded for frontmatter. */
  readonly title?: string;
}

/**
 * Normalised response consumed by the surface — ordered slide PNG sources
 * (each a ``data:image/png;base64,...`` URI usable directly as an ``<img src>``).
 */
export interface MarpPreviewResponse {
  readonly thumbnails: ReadonlyArray<string>;
}

/** BE wire shape of ``POST /api/v1/marp/preview-thumbnails`` (Wave 9). */
interface MarpPreviewThumbnailsWire {
  readonly thumbnails: ReadonlyArray<{ readonly index: number; readonly data_uri: string }>;
  readonly slide_count: number;
}

/**
 * Render the CURRENT (unsaved) per-slide outline into ordered slide
 * thumbnail PNGs so the proposal work-area surface can show the user how
 * the deck WILL look before they Confirm.
 *
 * Contract (BE Wave 9 — ``src/api/v1/endpoints/marp.py``):
 *   ``POST /api/v1/marp/preview-thumbnails``
 *     ``{ content_id?, markdown?, slides?, template_name?, proposal_id?, title? }``
 *     → ``{ thumbnails: [{ index, data_uri }], slide_count }``
 *
 * This is the SINGLE call site for the graphical preview, shared by the
 * proposal surface (``slides``) and the editor's Anteprima tab
 * (``markdown`` live buffer, falling back to ``content_id``). We flatten
 * the BE's ``{index, data_uri}`` records (sorted by ``index``) into the
 * plain ordered ``string[]`` the surface renders as ``<img src>``. Only
 * the fields the caller supplies are sent so the BE applies its
 * precedence chain on a clean payload.
 */
export async function previewMarpOutline(
  body: MarpPreviewRequest,
): Promise<MarpPreviewResponse> {
  const wire = await apiClient.post<MarpPreviewThumbnailsWire>(
    "/marp/preview-thumbnails",
    {
      ...(body.markdown !== undefined ? { markdown: body.markdown } : {}),
      ...(body.slides ? { slides: body.slides } : {}),
      ...(body.content_id ? { content_id: body.content_id } : {}),
      ...(body.template_name ? { template_name: body.template_name } : {}),
      ...(body.proposal_id ? { proposal_id: body.proposal_id } : {}),
      ...(body.title ? { title: body.title } : {}),
    },
  );
  const thumbnails = [...(wire.thumbnails ?? [])]
    .sort((a, b) => a.index - b.index)
    .map((t) => t.data_uri);
  return { thumbnails };
}

// ─── Wave 10 — structured per-slide outline (Editor tab) ────────────────

/**
 * Wire shape of a single slide in the saved-deck structured outline.
 *
 * Mirrors the ``@ando/ui`` ``SlideObj`` ({title, body, layout, image_prompt})
 * plus the BE-only ``image_kind`` discriminator (``ai`` / ``ai-bg`` /
 * ``ai-grid`` / ``none``). ``image_prompt`` follows the BE/UI contract:
 * ``string | null`` (null ⇒ image-less slide). Kept local to this module so
 * it stays free of a ``@ando/ui`` import (it lives in the apps layer
 * alongside the other marp wrappers).
 */
export interface MarpSlideObj {
  readonly title: string;
  readonly body: string;
  readonly layout: string;
  readonly image_prompt: string | null;
  /** BE-side image placeholder kind; round-tripped untouched. */
  readonly image_kind?: string;
}

/** Response shape for ``GET /api/v1/marp/{id}/outline``. */
export interface MarpOutlineResponse {
  readonly slides: ReadonlyArray<MarpSlideObj>;
  /** Active template slug for the deck, or ``null`` when none persisted. */
  readonly template_name: string | null;
}

/**
 * Fetch the structured per-slide outline for a SAVED deck so the Editor tab
 * can render the SAME ``SlideOutlineEditor`` used at creation instead of the
 * raw Marp markdown blob.
 *
 * Contract (BE Wave 10 — ``src/api/v1/endpoints/marp.py``):
 *   ``GET /api/v1/marp/{id}/outline`` → ``{ slides, template_name }``.
 */
export async function getMarpOutline(docId: string): Promise<MarpOutlineResponse> {
  return await apiClient.get<MarpOutlineResponse>(
    `/marp/${encodeURIComponent(docId)}/outline`,
  );
}

/**
 * Persist the edited per-slide outline. The BE re-renders the deck
 * (``render_deck``) and rewrites the ``.marp.md`` sidecar in place.
 *
 * Called debounced (~1500ms) from ``DeckOutlineEditorMount`` — same cadence
 * as ``putMarpMarkdown`` so the network sees one write per settle, not one
 * per keystroke.
 *
 * Contract: ``PUT /api/v1/marp/{id}/outline`` body ``{ slides }`` → 204.
 */
export async function putMarpOutline(
  docId: string,
  slides: ReadonlyArray<MarpSlideObj>,
): Promise<void> {
  await apiClient.put<void>(`/marp/${encodeURIComponent(docId)}/outline`, {
    slides,
  });
}

// ── OfficeCLI deck manual-edit outline (June 2026) ─────────────────────────
//
// Sibling of getMarpOutline/putMarpOutline but for OfficeCLI-generated decks
// (no .marp.md sidecar). Drives the SAME SlideOutlineEditor → on save the BE
// regenerates the deck via OfficeCLI and overwrites it in place. BE:
// ``src/api/v1/endpoints/presentations.py``.

/** Response shape for ``GET /api/v1/presentations/{id}/outline``. */
export interface PresentationOutlineResponse {
  readonly slides: ReadonlyArray<MarpSlideObj>;
  /** Deck title (filename without extension) to seed the title field. */
  readonly title: string;
  /** Allowed layout names for the editor dropdown (BE-sourced). */
  readonly layout_options: ReadonlyArray<string>;
  /** ``true`` = lossless (persisted spec); ``false`` = re-derived from pptx. */
  readonly from_spec: boolean;
}

/** Response shape for ``PUT /api/v1/presentations/{id}/outline``. */
export interface PresentationOutlineSaveResponse {
  readonly success: boolean;
  readonly bytes_written: number;
  /** New object ETag — used to cache-bust the PDF preview after regenerate. */
  readonly etag: string | null;
}

/**
 * Fetch the structured per-slide outline for a SAVED OfficeCLI deck so the
 * manual editor can render the same ``SlideOutlineEditor`` used at creation.
 * ``GET /api/v1/presentations/{id}/outline`` → ``{ slides, title, … }``.
 */
export async function getPresentationOutline(
  docId: string,
): Promise<PresentationOutlineResponse> {
  return await apiClient.get<PresentationOutlineResponse>(
    `/presentations/${encodeURIComponent(docId)}/outline`,
  );
}

/**
 * Persist the manually-edited outline. The BE regenerates the deck via
 * OfficeCLI and overwrites the SAME MinIO object in place (stable doc_id).
 * ``PUT /api/v1/presentations/{id}/outline`` body ``{ slides }``.
 */
export async function putPresentationOutline(
  docId: string,
  slides: ReadonlyArray<MarpSlideObj>,
): Promise<PresentationOutlineSaveResponse> {
  return await apiClient.put<PresentationOutlineSaveResponse>(
    `/presentations/${encodeURIComponent(docId)}/outline`,
    { slides },
  );
}

/** Args for {@link aiEditMarpSlide}. */
export interface AiEditMarpSlideArgs {
  /** The slide the user wants the AI to revise (current field values). */
  readonly slide: MarpSlideObj;
  /** Natural-language instruction (e.g. "rendi più formale"). */
  readonly instruction: string;
  /** Optional short deck context (title / summary) to ground the edit. */
  readonly deckContext?: string;
}

/**
 * Ask the AI to revise a single slide. The BE may change any of
 * ``title`` / ``body`` / ``layout`` / ``image_prompt`` and returns the full
 * updated slide, which the caller swaps in and re-persists.
 *
 * Contract: ``POST /api/v1/marp/{id}/ai-edit-slide``
 *   body ``{ slide, instruction, deck_context? }`` → ``{ slide }``.
 */
export async function aiEditMarpSlide(
  docId: string,
  { slide, instruction, deckContext }: AiEditMarpSlideArgs,
): Promise<MarpSlideObj> {
  const res = await apiClient.post<{ slide: MarpSlideObj }>(
    `/marp/${encodeURIComponent(docId)}/ai-edit-slide`,
    {
      slide,
      instruction,
      ...(deckContext ? { deck_context: deckContext } : {}),
    },
  );
  return res.slide;
}

// ─── Wave 4 — Templates surface ─────────────────────────────────────────

/**
 * Scope of a custom template — who can pick it from the dropdown.
 * Mirrors the BE `custom_chrome_templates.owner_type` column.
 */
export type MarpTemplateOwnerScope = "user" | "org";

export interface MarpCustomTemplate {
  /** Slug used as the BE identifier (e.g. ``"my-blue-deck"``). */
  readonly slug: string;
  /** Optional user-facing one-liner shown in the picker / settings list. */
  readonly description?: string;
  /** Whether the row is personal (``"user"``) or org-wide (``"org"``). */
  readonly scope: MarpTemplateOwnerScope;
}

/** Response shape for `GET /api/v1/templates/marp`. */
export interface MarpTemplateListResponse {
  /** Slugs of the four built-in themes (BE source of truth). */
  readonly builtins: ReadonlyArray<string>;
  /** Custom rows the calling user (or their org) has uploaded. */
  readonly customs: ReadonlyArray<MarpCustomTemplate>;
}

/**
 * Fetch the list of templates the user can pick from.
 *
 * Wired to the Wave 4 BE-Templates surface — when the endpoint isn't
 * deployed yet the caller (`useMarpTemplates`) maps the 404 into the
 * "builtins-only" fallback so the FE never blocks on the BE catching up.
 */
export async function listMarpTemplates(): Promise<MarpTemplateListResponse> {
  return await apiClient.get<MarpTemplateListResponse>("/templates/marp");
}

/** Body for `POST /api/v1/templates/marp/upload-css`. */
export interface UploadCustomCssOptions {
  readonly slug: string;
  readonly css: string;
  readonly description?: string;
  readonly scope: MarpTemplateOwnerScope;
}

/** Response shape for the upload-css endpoint. */
export interface UploadCustomCssResponse {
  readonly slug: string;
}

/**
 * Persist a raw CSS string as a new (or replacement) custom template.
 *
 * Returns the slug the BE actually stored — the BE may sanitize the
 * supplied slug (lowercase + kebab-case + dedupe), so the caller should
 * use the returned value as the canonical identifier going forward.
 */
export async function uploadCustomCss(
  opts: UploadCustomCssOptions,
): Promise<UploadCustomCssResponse> {
  return await apiClient.post<UploadCustomCssResponse>(
    "/templates/marp/upload-css",
    {
      slug: opts.slug,
      css: opts.css,
      description: opts.description,
      scope: opts.scope,
    },
  );
}

/**
 * Response shape for `POST /api/v1/templates/marp/extract-from-pptx`.
 *
 * The BE opens the uploaded `.pptx`, sniffs dominant accent + body + heading
 * colors + font family, then emits a Marp CSS string that approximates the
 * brand. `tokens` is the loose "what we extracted" map shown to the user as
 * a preview swatch table — keys are stable (`brand_bg`, `accent`,
 * `title_font`, ...) so the FE can render a typed table.
 */
export interface ExtractCssFromPptxResponse {
  readonly css: string;
  readonly suggested_slug: string;
  readonly tokens: Record<string, string>;
}

/**
 * Extract a Marp CSS template from a user-supplied PowerPoint file.
 *
 * Uses multipart/form-data so the BE can stream the bytes through
 * python-pptx without a base64 round-trip. We bypass the JSON helpers
 * (`apiClient.post`) for the same reason multipart upload sites elsewhere
 * do: we need the browser to set the Content-Type boundary itself.
 */
export async function extractCssFromPptx(
  file: File,
): Promise<ExtractCssFromPptxResponse> {
  const form = new FormData();
  form.append("file", file, file.name);
  return await apiClient.request<ExtractCssFromPptxResponse>(
    "/templates/marp/extract-from-pptx",
    {
      method: "POST",
      body: form,
    },
  );
}

// ─── Region edit — in-editor "ask AI to change THIS part" ──────────────
// POST /documents/{docId}/region-edit: rewrites the selection AND proposes it
// as a tracked change → the SAME doc_diff_payload redline rail as chat edits.
// Used as the AiTextPopover `onSubmit` transport for docx/xlsx.

export interface RegionEditResult {
  readonly proposal_id: string;
  readonly rewritten: string;
  readonly rationale?: string | null;
}

export async function regionEditDocument(
  docId: string,
  args: {
    mime: "docx" | "xlsx" | "pptx";
    selectedText: string;
    instruction: string;
    context?: string;
    conversationId?: string;
    /** OfficeCLI path of the exact selected element (e.g. /Sheet1/B5) so the
     * rewrite targets that cell precisely instead of a whole-doc find/replace. */
    selectedPath?: string;
  },
): Promise<RegionEditResult> {
  return await apiClient.post<RegionEditResult>(
    `/documents/${encodeURIComponent(docId)}/region-edit`,
    {
      mime: args.mime,
      selected_text: args.selectedText,
      instruction: args.instruction,
      context: args.context ?? "",
      conversation_id: args.conversationId ?? null,
      selected_path: args.selectedPath ?? null,
    },
  );
}

// ─── FASE 2 — uploadable graphical OOXML templates ─────────────────────
// Upload a branded .docx/.xlsx/.pptx whose {{placeholders}} generation fills
// via OfficeCLI merge. Mirrors the extractCssFromPptx multipart pattern.

export interface OoxmlTemplateUploadResponse {
  readonly slug: string;
  readonly document_type: string;
  readonly mode: string;
  readonly placeholders: readonly string[];
  /** True when the AI tokenizer made a raw deck merge-ready (mode='tokenized'). */
  readonly merge_ready?: boolean;
  readonly scope: string;
}

export async function uploadOoxmlTemplate(
  file: File,
  opts: { slug: string; scope?: "user" | "org" },
): Promise<OoxmlTemplateUploadResponse> {
  const form = new FormData();
  form.append("file", file, file.name);
  form.append("slug", opts.slug);
  form.append("scope", opts.scope ?? "user");
  return await apiClient.request<OoxmlTemplateUploadResponse>(
    "/templates/ooxml/upload",
    { method: "POST", body: form },
  );
}

export interface OoxmlTemplateItem {
  readonly slug: string;
  readonly scope: string;
  readonly document_type: string | null;
  readonly mode: string | null;
  readonly placeholders: readonly string[];
  readonly description: string;
  /**
   * True for first-party Ando BUILTIN templates (shipped with the app,
   * resolved from disk). The BE PREPENDS these to the list; the picker
   * renders them at the top with an "Ando" badge. Absent/false for the
   * caller's own user/org uploads.
   */
  readonly is_builtin?: boolean;
  /** Human display label (builtins carry e.g. "Ando — Deck"; uploads omit it). */
  readonly name?: string;
  /** TEST-ONLY curated entry (e.g. SlidesGo) — badged, remove before prod. */
  readonly test_only?: boolean;
  /** True when a slide-1 PNG preview is available (cookie-authed `<img>`). */
  readonly has_thumbnail?: boolean;
}

export async function listOoxmlTemplates(): Promise<{
  readonly templates: readonly OoxmlTemplateItem[];
}> {
  return await apiClient.get<{ templates: readonly OoxmlTemplateItem[] }>(
    "/templates/ooxml",
  );
}

// ─── Visual style presets (GET /api/v1/styles) ─────────────────────────
// Built-in declarative ChromeSpec presets the generator can render a
// document_create against (Word / xlsx / PowerPoint). Distinct from the
// uploadable {{placeholder}} OOXML templates above: a STYLE is a themable
// look (accent / ink / surface + light/dark), a TEMPLATE is a fixed-fill
// branded file. The two are mutually exclusive at execute time (the BE
// reads ``_style_id`` OR ``_ooxml_template_slug``; template wins).

/** Coarse grouping shown as section headers in the picker / gallery. */
export type StyleCategory = "dark" | "light" | "warm" | "vivid" | "bw" | "mixed";

/**
 * A single style preset row (BE: ``style_presets.list_styles()``).
 *
 * ``accent`` / ``ink`` / ``surface`` are 6-digit RRGGBB hex strings WITHOUT
 * the leading ``#`` (BE contract) — the FE prepends ``#`` when building the
 * swatch so we never double it. ``is_dark`` lets the swatch render a sensible
 * contrast border without parsing luminance client-side.
 */
export interface StylePreset {
  readonly id: string;
  readonly name: string;
  readonly category: StyleCategory;
  readonly is_dark: boolean;
  /** RRGGBB (no leading #). */
  readonly accent: string;
  /** RRGGBB (no leading #). */
  readonly ink: string;
  /** RRGGBB (no leading #). */
  readonly surface: string;
}

/** Wire shape of ``GET /api/v1/styles``. */
interface StyleListWire {
  readonly styles: ReadonlyArray<StylePreset>;
}

/**
 * Fetch the built-in visual style presets the document generator supports.
 *
 * Contract (shared BE/FE): ``GET /api/v1/styles`` →
 *   ``{ styles: [{ id, name, category, is_dark, accent, ink, surface }] }``
 * with Ando's default preset FIRST. Consumed by ``useStyles`` (TanStack)
 * which degrades to an empty list on 404 so the picker can fall back to the
 * implicit "Ando default" choice while the BE rolls out.
 */
export async function getStyles(): Promise<ReadonlyArray<StylePreset>> {
  const wire = await apiClient.get<StyleListWire>("/styles");
  return wire.styles ?? [];
}

/**
 * Learn a brand-new visual style by uploading a reference document the user
 * already owns (a branded ``.pptx`` / ``.docx`` / ``.xlsx``). The BE sniffs
 * its dominant palette + typography, materialises a {@link StylePreset}, and
 * PERSISTS it per user so it immediately shows up in {@link getStyles} and is
 * selectable in generation EXACTLY like a built-in preset
 * (``content["_style_id"] = <learned id>``).
 *
 * Contract (shared BE/FE):
 *   ``POST /api/v1/styles/learn`` (multipart: ``file``, optional ``name``)
 *     → ``{ id, name, category, is_dark, accent, ink, surface }``
 *       — the SAME entry shape as a row of ``GET /api/v1/styles``.
 *
 * We post multipart/form-data (mirroring {@link extractCssFromPptx} /
 * {@link uploadOoxmlTemplate}) so the browser streams the bytes and sets the
 * Content-Type boundary itself — no base64 round-trip, no JSON helper.
 */
export async function learnStyleFromSample(
  file: File,
  name?: string,
): Promise<StylePreset> {
  const form = new FormData();
  form.append("file", file, file.name);
  const trimmed = name?.trim();
  if (trimmed) form.append("name", trimmed);
  return await apiClient.request<StylePreset>("/styles/learn", {
    method: "POST",
    body: form,
  });
}

// ─── Wave 6 — per-image regenerate ─────────────────────────────────────

/**
 * Body shape for `POST /api/v1/marp/{id}/regenerate-image`.
 *
 * `slide_index` + `placeholder_index` are 0-indexed (BE counts
 * `ai:`/`ai-bg:`/`ai-grid:` placeholders top-to-bottom). `new_prompt`
 * is optional — when omitted the BE re-uses the placeholder's existing
 * prompt with a fresh seed so the regenerated image differs from the
 * original. `illustration_style` is also optional and threads through
 * to `image_resolver.resolve_image_placeholders` so the new image
 * picks up the latest user choice from the editor.
 */
export interface RegenerateMarpImageRequest {
  readonly slide_index: number;
  readonly placeholder_index: number;
  readonly new_prompt?: string;
  readonly illustration_style?: string;
}

/**
 * Response from the regenerate endpoint (BE-Endpoints agent, Wave 6).
 *
 * `preview_invalidation_token` is a server-generated nonce that the FE
 * bumps onto the iframe cache-buster query so the browser re-fetches
 * the freshly-compiled PPTX preview after the regeneration completes.
 */
export interface RegenerateMarpImageResponse {
  readonly success: boolean;
  readonly pptx_size_bytes: number;
  readonly regenerated_placeholder_index: number;
  readonly preview_invalidation_token: string;
}

/**
 * Re-runs a single image placeholder through the BE image pipeline.
 *
 * Wired to the BE-Endpoints surface (Wave 6, Track 3). The endpoint
 * loads the sidecar markdown, locates the Nth AI placeholder (0-indexed
 * scan across all `ai:`/`ai-bg:`/`ai-grid:` patterns), regenerates only
 * that image, refreshes the cached PPTX in MinIO, and returns a fresh
 * invalidation token the FE uses to bust the iframe cache.
 */
export async function regenerateImage(
  contentId: string,
  body: RegenerateMarpImageRequest,
): Promise<RegenerateMarpImageResponse> {
  return await apiClient.post<RegenerateMarpImageResponse>(
    `/marp/${encodeURIComponent(contentId)}/regenerate-image`,
    body,
  );
}

/**
 * Build an absolute, anchor-friendly URL for the dual-download buttons.
 *
 * The BE endpoint surface (per Wave 4 contract):
 *
 *   GET /api/v1/marp/{content_id}/download/frozen.pptx     ← Marp-rendered
 *   GET /api/v1/marp/{content_id}/download/editable.pptx   ← Pandoc-rendered
 *
 * Both are cookie-authed; the `<a download>` element re-uses the BFF
 * session without any header gymnastics. We resolve against
 * `apiClient.getBaseUrl()` so the URL is correct under Tauri WKWebView
 * (where `window.location.origin === "tauri://localhost"` would 404).
 */
export function getDownloadUrl(
  contentId: string,
  format: "frozen" | "editable",
): string {
  const baseUrl = apiClient.getBaseUrl();
  const safeId = encodeURIComponent(contentId);
  const path = `/marp/${safeId}/download/${format}.pptx`;
  // ``baseUrl`` may itself be relative (``/api/v1`` in web mode); the URL
  // constructor needs an absolute reference. Use the page origin as a
  // fallback so ``new URL('/api/v1/x', 'http://host')`` works. Mirrors
  // the resolution logic in ``$capability.tsx::resolveOAuthRedirect``.
  if (/^https?:\/\//i.test(baseUrl)) {
    return `${baseUrl}${path}`;
  }
  const pageOrigin =
    typeof window !== "undefined" ? window.location.origin : "http://localhost";
  const prefix = baseUrl.startsWith("/") ? "" : "/";
  return `${pageOrigin}${prefix}${baseUrl}${path}`;
}
